/**
 * PortalLayout - Pre-wired layout with Header, Sidebar, Footer
 *
 * Standard portal pages wrap content in <PortalLayout> to get the full layout
 * (header, sidebar, footer) with zero boilerplate. Sidebar state is managed internally.
 *
 * For full customization, use AppLayout directly with your own slots.
 *
 * Usage: <PortalLayout>{page content}</PortalLayout>
 */

import { AppLayout, BackToTop, Sheet, SheetContent, Toaster } from '@/components/ui';
import React, { useMemo } from 'react';
import { CookieConsent } from '../CookieConsent';
import { EnhancedAppHeader } from '../EnhancedAppHeader';
import { FlashMessages } from '../FlashMessages';
import { FullPageLoading } from '../LoadingSkeleton';
import { PortalSidebar } from '../PortalSidebar';
import { SupportChatWidget } from '../SupportChatWidget';
import { PortalFooter } from './PortalFooter';
import { usePortalLayout } from './PortalLayout.hook';
import { usePortalLayoutNavigating, useSetPortalLayoutSidebarOpen } from './PortalLayout.store';

export interface PortalLayoutProps {
  children: React.ReactNode;
  /** Override default header */
  header?: React.ReactNode;
  /** Override default sidebar */
  sidebar?: React.ReactNode;
  /** Override default footer */
  footer?: React.ReactNode;
  /** Optional banner slot */
  banner?: React.ReactNode;
  /** Pass-through to AppLayout contentClassName */
  contentClassName?: string;
  /** Pass-through to AppLayout mainClassName */
  mainClassName?: string;
  /** Additional className for outer wrapper */
  className?: string;
}

export const PortalLayout = React.forwardRef<HTMLDivElement, PortalLayoutProps>(
  (
    { children, header, sidebar, footer, banner, contentClassName, mainClassName, className },
    ref,
  ) => {
    const { footerData, sidebarOpen, toggleSidebar, isMobile } = usePortalLayout();
    const isNavigating = usePortalLayoutNavigating();
    const setSidebarOpen = useSetPortalLayoutSidebarOpen();

    // Mobile: Sheet drawer (off-canvas from left)
    const mobileSidebar = isMobile && (
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-[300px]" hideClose>
          <PortalSidebar isOpen={true} expandedWidth="100%" />
        </SheetContent>
      </Sheet>
    );

    // Desktop: persistent sidebar in AppLayout slot
    const desktopSidebar = useMemo(
      () => (!isMobile ? sidebar ?? <PortalSidebar isOpen={sidebarOpen} expandedWidth="360px" /> : null),
      [isMobile, sidebar, sidebarOpen]
    );

    // Memoize header element
    const headerElement = useMemo(
      () => header ?? <EnhancedAppHeader onToggleSidebar={toggleSidebar} showSidebarToggle={true} />,
      [header, toggleSidebar]
    );

    // Memoize footer element
    const footerElement = useMemo(
      () => footer ?? <PortalFooter version={footerData?.version} buildNumber={footerData?.buildNumber} />,
      [footer, footerData?.version, footerData?.buildNumber]
    );

    // Memoize slots object
    const slotsObject = useMemo(
      () => ({
        sidebar: desktopSidebar,
        header: headerElement,
        footer: footerElement,
        banner,
      }),
      [desktopSidebar, headerElement, footerElement, banner]
    );

    return (
      <div ref={ref} className={className}>
        {mobileSidebar}
        <AppLayout
          sidebarOpen={sidebarOpen}
          onToggleSidebar={toggleSidebar}
          isLoading={isNavigating}
          loadingComponent={<FullPageLoading />}
          contentClassName={contentClassName}
          mainClassName={mainClassName}
          slots={slotsObject}
        >
          {children}
        </AppLayout>
        <BackToTop />
        <CookieConsent />
        <SupportChatWidget />
        <FlashMessages />
        <Toaster />
      </div>
    );
  },
);

PortalLayout.displayName = 'PortalLayout';
